/**
 * State del Lifecycle Designer (B.6.d.9.c).
 *
 * Análogo a `state.ts` del Artifact Designer, pero el modelo es
 * distinto: en vez de un árbol recursivo de secciones, el lifecycle
 * tiene una lista plana de phases, cada phase con su lista plana de
 * subphases y expected_artifacts.
 *
 * El nodo seleccionado en el árbol puede ser:
 *   - `{kind: 'phase', phaseCode}`
 *   - `{kind: 'subphase', phaseCode, subphaseCode}`
 *   - `{kind: 'expected_artifact', phaseCode, subphaseCode, templateKey}`
 *
 * Mismo patrón de autosave: el orchestrator escucha cambios al
 * `definition` y dispara persist debounced.
 */

import { useReducer, type Dispatch } from 'react'

import type {
  ExpectedArtifact,
  LifecycleDefinition,
  LifecycleI18nBlock,
  LifecyclePhase,
  LifecyclePhaseI18n,
  LifecycleSubphase,
  LifecycleSubphaseI18n,
} from '../../../api/templates'
import { editingLocale, type SupportedLocale } from '../../../i18n/config'

// ─── Selección ──────────────────────────────────────────────────────────────
export type SelectedLifecycleNode =
  | { kind: 'phase'; phaseCode: string }
  | { kind: 'subphase'; phaseCode: string; subphaseCode: string }
  | {
      kind: 'expected_artifact'
      phaseCode: string
      subphaseCode: string
      templateKey: string
    }

// ─── State ──────────────────────────────────────────────────────────────────
export interface LifecycleDesignerState {
  definition: LifecycleDefinition
  selectedNode: SelectedLifecycleNode | null
  activeLocale: SupportedLocale
  dirty: boolean
}

// ─── Actions ────────────────────────────────────────────────────────────────
export type LifecycleDesignerAction =
  | { type: 'SET_DEFINITION'; definition: LifecycleDefinition }
  | { type: 'SELECT_NODE'; node: SelectedLifecycleNode | null }
  | { type: 'SET_LOCALE'; locale: SupportedLocale }
  | { type: 'MARK_CLEAN' }
  // Lifecycle global i18n (name + description del template)
  | {
      type: 'UPDATE_GLOBAL_I18N'
      locale: SupportedLocale
      patch: Partial<Pick<LifecycleI18nBlock, 'name' | 'description'>>
    }
  | { type: 'ADD_LOCALE'; locale: SupportedLocale }
  // Phases
  | { type: 'ADD_PHASE' }
  | { type: 'REMOVE_PHASE'; phaseCode: string }
  | { type: 'MOVE_PHASE'; phaseCode: string; direction: -1 | 1 }
  | {
      type: 'UPDATE_PHASE_STRUCTURAL'
      phaseCode: string
      patch: Partial<
        Pick<
          LifecyclePhase,
          'default_contract_mode' | 'contract_mode_overridable'
        >
      >
    }
  | {
      type: 'UPDATE_PHASE_I18N'
      phaseCode: string
      locale: SupportedLocale
      patch: Partial<
        Pick<LifecyclePhaseI18n, 'name' | 'description' | 'gate_description'>
      >
    }
  // Subphases
  | { type: 'ADD_SUBPHASE'; phaseCode: string }
  | { type: 'REMOVE_SUBPHASE'; phaseCode: string; subphaseCode: string }
  | {
      type: 'MOVE_SUBPHASE'
      phaseCode: string
      subphaseCode: string
      direction: -1 | 1
    }
  | {
      type: 'UPDATE_SUBPHASE_I18N'
      phaseCode: string
      subphaseCode: string
      locale: SupportedLocale
      patch: Partial<Pick<LifecycleSubphaseI18n, 'name' | 'description'>>
    }
  // Doc 21 (Approval Workflow), Fase 3 — campos estructurales del
  // subphase que NO son i18n. Por ahora solo
  // `approval_workflow_template_key`. El override embebido se queda
  // para un editor más complejo en una iteración futura.
  | {
      type: 'UPDATE_SUBPHASE_STRUCTURAL'
      phaseCode: string
      subphaseCode: string
      patch: { approval_workflow_template_key?: string | null }
    }
  // Expected artifacts. En el modelo 1:1 (B.6.d.9.i+) `subphase_code`
  // identifica únicamente el deliverable dentro de la phase, así que no
  // hace falta `templateKey` como discriminador adicional.
  | {
      type: 'ADD_EXPECTED_ARTIFACT'
      phaseCode: string
      subphaseCode: string
      templateKey: string
    }
  | {
      type: 'REMOVE_EXPECTED_ARTIFACT'
      phaseCode: string
      subphaseCode: string
    }
  | {
      type: 'UPDATE_EXPECTED_ARTIFACT'
      phaseCode: string
      subphaseCode: string
      patch: Partial<
        Pick<
          ExpectedArtifact,
          | 'default_count'
          | 'is_optional'
          | 'user_can_add_more'
          | 'role_template_key'
        >
      >
    }
  // Deliverable = sub-fase + expected_artifact creados juntos (B.6.d.9.i).
  // El picker del Designer dispara esta acción única en lugar de
  // ADD_SUBPHASE + ADD_EXPECTED_ARTIFACT por separado.
  // `templateKey` acepta `null` por compat con el reducer/tipo legacy
  // (deliverables file-based de lifecycles publicados antes de OT-0007);
  // el picker actual siempre lo manda no-nulo — ver LifecycleArtifactPickerDialog.
  | {
      type: 'ADD_DELIVERABLE'
      phaseCode: string
      // OT-0007 §4.1 — requerido en los dos modos de producción: sin él
      // no hay forma de materializar el Artifact al crear el proyecto.
      // El backend lo enforce (`ExpectedArtifact.template_key: str`) y el
      // picker ya no despacha sin plantilla seleccionada.
      templateKey: string
      subphaseName: string
      defaultCount: number
      isOptional: boolean
      userCanAddMore: boolean
      roleTemplateKey: string | null
    }
  // Equivalente a remove_subphase + remove_expected_artifact (mismos
  // subphase_code en ambos lados).
  | {
      type: 'REMOVE_DELIVERABLE'
      phaseCode: string
      subphaseCode: string
    }
  // Limpia TODOS los deliverables (subphases + expected_artifacts) de
  // una phase. Usado al cambiar phase.default_contract_mode con
  // deliverables existentes incompatibles. (B.6.d.9.k)
  | { type: 'CLEAR_PHASE_DELIVERABLES'; phaseCode: string }

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Genera un phase_code único en la lista actual. Pattern `phase-N`. */
function generatePhaseCode(phases: LifecyclePhase[]): string {
  const taken = new Set(phases.map((p) => p.phase_code))
  let n = phases.length
  while (taken.has(`phase-${n}`)) n += 1
  return `phase-${n}`
}

/** Genera un subphase_code único dentro de una phase. Pattern
 * `<phase_code>.<n>`. Empieza en 1. */
function generateSubphaseCode(
  phaseCode: string,
  subphases: LifecycleSubphase[],
): string {
  const taken = new Set(subphases.map((sp) => sp.subphase_code))
  let n = subphases.length + 1
  while (taken.has(`${phaseCode}.${n}`)) n += 1
  return `${phaseCode}.${n}`
}

/** Reindex auto de phase_order para que sea 0..N-1 secuencial. */
function reindexPhaseOrders(phases: LifecyclePhase[]): LifecyclePhase[] {
  return phases.map((p, idx) =>
    p.phase_order === idx ? p : { ...p, phase_order: idx },
  )
}

/** Reindex de subphase_order dentro de una phase. */
function reindexSubphaseOrders(
  subphases: LifecycleSubphase[],
): LifecycleSubphase[] {
  return subphases.map((sp, idx) =>
    sp.subphase_order === idx ? sp : { ...sp, subphase_order: idx },
  )
}

/** Lookup helpers públicos. */
export function findPhase(
  definition: LifecycleDefinition,
  phaseCode: string,
): LifecyclePhase | undefined {
  return definition.phases.find((p) => p.phase_code === phaseCode)
}

export function findPhaseI18n(
  definition: LifecycleDefinition,
  phaseCode: string,
  locale: SupportedLocale,
): LifecyclePhaseI18n | undefined {
  return definition.i18n[locale]?.phases?.find(
    (p) => p.phase_code === phaseCode,
  )
}

export function findSubphase(
  definition: LifecycleDefinition,
  phaseCode: string,
  subphaseCode: string,
): LifecycleSubphase | undefined {
  return findPhase(definition, phaseCode)?.subphases?.find(
    (sp) => sp.subphase_code === subphaseCode,
  )
}

export function findSubphaseI18n(
  definition: LifecycleDefinition,
  phaseCode: string,
  subphaseCode: string,
  locale: SupportedLocale,
): LifecycleSubphaseI18n | undefined {
  return findPhaseI18n(definition, phaseCode, locale)?.subphases?.find(
    (sp) => sp.subphase_code === subphaseCode,
  )
}

export function findExpectedArtifact(
  definition: LifecycleDefinition,
  phaseCode: string,
  subphaseCode: string,
  templateKey: string,
): ExpectedArtifact | undefined {
  return findPhase(definition, phaseCode)?.expected_artifacts?.find(
    (a) => a.template_key === templateKey && a.subphase_code === subphaseCode,
  )
}

/** Renumera todos los `phase_code` y `subphase_code` para que sigan la
 * posición actual en la lista (B.6.d.9.k+).
 *
 * Pattern:
 *   - phase[i].phase_code   → `phase-${i}`
 *   - subphase[j] de phase  → `${phase_code}.${j + 1}`
 *
 * Cascadea los renames a:
 *   - expected_artifacts[].subphase_code (matching la sub-fase)
 *   - i18n.{lang}.phases[].phase_code
 *   - i18n.{lang}.phases[].subphases[].subphase_code
 *   - state.selectedNode (mantiene la selección viva tras el rename)
 *
 * Llamarlo después de cualquier MOVE/REMOVE; ADD no lo necesita
 * (los códigos nuevos ya nacen con el sufijo correcto).
 */
function renumberLifecycle(
  state: LifecycleDesignerState,
): LifecycleDesignerState {
  const sorted = [...state.definition.phases].sort(
    (a, b) => a.phase_order - b.phase_order,
  )
  // oldPhaseCode → newPhaseCode
  const phaseRename = new Map<string, string>()
  // oldPhaseCode → Map<oldSubCode, newSubCode>
  const subRenameByOldPhase = new Map<string, Map<string, string>>()

  const renumberedPhases: LifecyclePhase[] = sorted.map((p, i) => {
    const newPhaseCode = `phase-${i}`
    if (newPhaseCode !== p.phase_code) {
      phaseRename.set(p.phase_code, newPhaseCode)
    }

    const sortedSubs = [...(p.subphases ?? [])].sort(
      (a, b) => a.subphase_order - b.subphase_order,
    )
    const subRename = new Map<string, string>()
    const renumberedSubs: LifecycleSubphase[] = sortedSubs.map((sp, j) => {
      const newSubCode = `${newPhaseCode}.${j + 1}`
      if (newSubCode !== sp.subphase_code) {
        subRename.set(sp.subphase_code, newSubCode)
      }
      return {
        ...sp,
        subphase_code: newSubCode,
        subphase_order: j,
      }
    })
    if (subRename.size > 0) {
      subRenameByOldPhase.set(p.phase_code, subRename)
    }

    const renumberedArts = (p.expected_artifacts ?? []).map((a) => {
      const next = subRename.get(a.subphase_code)
      return next ? { ...a, subphase_code: next } : a
    })

    return {
      ...p,
      phase_code: newPhaseCode,
      phase_order: i,
      subphases: renumberedSubs,
      expected_artifacts: renumberedArts,
    }
  })

  // Actualizar i18n.{lang}.phases[].phase_code y subphases[].subphase_code
  const newI18n: LifecycleDefinition['i18n'] = {}
  for (const [lang, block] of Object.entries(state.definition.i18n)) {
    if (!block) continue
    const newPhases = (block.phases ?? []).map((ph) => {
      const newPhCode = phaseRename.get(ph.phase_code) ?? ph.phase_code
      const subRename =
        subRenameByOldPhase.get(ph.phase_code) ?? new Map<string, string>()
      const newSubs = (ph.subphases ?? []).map((sp) => ({
        ...sp,
        subphase_code: subRename.get(sp.subphase_code) ?? sp.subphase_code,
      }))
      return {
        ...ph,
        phase_code: newPhCode,
        subphases: newSubs,
      }
    })
    newI18n[lang as SupportedLocale] = { ...block, phases: newPhases }
  }

  // Mantener viva la selección si referenciaba códigos viejos.
  let nextSelected = state.selectedNode
  if (nextSelected) {
    const oldPhaseCode =
      'phaseCode' in nextSelected ? nextSelected.phaseCode : null
    const newPhaseCode = oldPhaseCode
      ? (phaseRename.get(oldPhaseCode) ?? oldPhaseCode)
      : null
    if (
      nextSelected.kind === 'phase' &&
      newPhaseCode &&
      newPhaseCode !== nextSelected.phaseCode
    ) {
      nextSelected = { kind: 'phase', phaseCode: newPhaseCode }
    } else if (
      (nextSelected.kind === 'subphase' ||
        nextSelected.kind === 'expected_artifact') &&
      oldPhaseCode &&
      newPhaseCode
    ) {
      const subRename =
        subRenameByOldPhase.get(oldPhaseCode) ?? new Map<string, string>()
      const newSubCode =
        subRename.get(nextSelected.subphaseCode) ?? nextSelected.subphaseCode
      if (
        newPhaseCode !== nextSelected.phaseCode ||
        newSubCode !== nextSelected.subphaseCode
      ) {
        nextSelected =
          nextSelected.kind === 'subphase'
            ? {
                kind: 'subphase',
                phaseCode: newPhaseCode,
                subphaseCode: newSubCode,
              }
            : {
                kind: 'expected_artifact',
                phaseCode: newPhaseCode,
                subphaseCode: newSubCode,
                templateKey: nextSelected.templateKey,
              }
      }
    }
  }

  return {
    ...state,
    definition: {
      ...state.definition,
      phases: renumberedPhases,
      i18n: newI18n,
    },
    selectedNode: nextSelected,
  }
}

// ─── Inmutable update de la i18n.{lang}.phases[phase_code] ─────────────────
function updatePhaseI18n(
  definition: LifecycleDefinition,
  locale: SupportedLocale,
  phaseCode: string,
  transform: (cur: LifecyclePhaseI18n | undefined) => LifecyclePhaseI18n,
): LifecycleDefinition {
  const block = definition.i18n[locale]
  if (!block) return definition
  const phases = block.phases ?? []
  const existing = phases.find((p) => p.phase_code === phaseCode)
  const next = transform(existing)
  const nextPhases = existing
    ? phases.map((p) => (p.phase_code === phaseCode ? next : p))
    : [...phases, next]
  return {
    ...definition,
    i18n: {
      ...definition.i18n,
      [locale]: { ...block, phases: nextPhases },
    },
  }
}

/** Renombra phase_code en i18n cuando se reordena (no aplica hoy
 * porque move solo cambia phase_order, no el code, pero queda como
 * helper para el futuro). */

// ─── Reducer ────────────────────────────────────────────────────────────────
export function lifecycleDesignerReducer(
  state: LifecycleDesignerState,
  action: LifecycleDesignerAction,
): LifecycleDesignerState {
  switch (action.type) {
    case 'SET_DEFINITION':
      return {
        definition: action.definition,
        selectedNode:
          action.definition.phases[0]
            ? { kind: 'phase', phaseCode: action.definition.phases[0].phase_code }
            : null,
        // La versión nueva puede no tener el idioma que se estaba editando.
        activeLocale: editingLocale(Object.keys(action.definition.i18n), state.activeLocale),
        dirty: false,
      }
    case 'SELECT_NODE':
      return { ...state, selectedNode: action.node }
    case 'SET_LOCALE':
      return { ...state, activeLocale: action.locale }
    case 'MARK_CLEAN':
      return { ...state, dirty: false }

    case 'UPDATE_GLOBAL_I18N': {
      const block = state.definition.i18n[action.locale]
      const next: LifecycleI18nBlock = block
        ? { ...block, ...action.patch }
        : {
            name: action.patch.name ?? '',
            ...(action.patch.description !== undefined
              ? { description: action.patch.description }
              : {}),
            phases: [],
          }
      return {
        ...state,
        definition: {
          ...state.definition,
          i18n: { ...state.definition.i18n, [action.locale]: next },
        },
        dirty: true,
      }
    }

    case 'ADD_LOCALE': {
      if (state.definition.i18n[action.locale]) return state
      // Seed con el name del otro locale (no-vacío para min_length=1).
      const otherName =
        state.definition.i18n.es?.name ??
        state.definition.i18n.en?.name ??
        'Untitled'
      const otherDescription =
        state.definition.i18n.es?.description ??
        state.definition.i18n.en?.description
      return {
        ...state,
        definition: {
          ...state.definition,
          i18n: {
            ...state.definition.i18n,
            [action.locale]: {
              name: otherName,
              ...(otherDescription !== undefined
                ? { description: otherDescription }
                : {}),
              phases: [],
            },
          },
        },
        dirty: true,
      }
    }

    case 'ADD_PHASE': {
      const phaseCode = generatePhaseCode(state.definition.phases)
      const phaseOrder = state.definition.phases.length
      const newPhase: LifecyclePhase = {
        phase_code: phaseCode,
        phase_order: phaseOrder,
        default_contract_mode: 'internal',
        contract_mode_overridable: false,
        subphases: [],
        expected_artifacts: [],
      }
      // Seed i18n entry en cada locale activo (name vacío — el user lo
      // llena, pero tiene que existir un entry por phase_code).
      const nextI18n: LifecycleDefinition['i18n'] = { ...state.definition.i18n }
      for (const loc of Object.keys(nextI18n) as Array<SupportedLocale>) {
        const block = nextI18n[loc]
        if (!block) continue
        nextI18n[loc] = {
          ...block,
          phases: [
            ...(block.phases ?? []),
            { phase_code: phaseCode, name: phaseCode },
          ],
        }
      }
      return {
        ...state,
        definition: {
          ...state.definition,
          phases: [...state.definition.phases, newPhase],
          i18n: nextI18n,
        },
        selectedNode: { kind: 'phase', phaseCode },
        dirty: true,
      }
    }

    case 'REMOVE_PHASE': {
      const phases = state.definition.phases.filter(
        (p) => p.phase_code !== action.phaseCode,
      )
      const reindexed = reindexPhaseOrders(phases)
      // Limpiar i18n.phases[].phase_code matcheante en cada locale.
      const nextI18n: LifecycleDefinition['i18n'] = {}
      for (const [loc, block] of Object.entries(state.definition.i18n)) {
        if (!block) continue
        nextI18n[loc as SupportedLocale] = {
          ...block,
          phases:
            block.phases?.filter((p) => p.phase_code !== action.phaseCode) ??
            [],
        }
      }
      const nextSelected =
        state.selectedNode &&
        ('phaseCode' in state.selectedNode &&
          state.selectedNode.phaseCode === action.phaseCode)
          ? reindexed[0]
            ? { kind: 'phase' as const, phaseCode: reindexed[0].phase_code }
            : null
          : state.selectedNode
      const removed_state: LifecycleDesignerState = {
        ...state,
        definition: {
          ...state.definition,
          phases: reindexed,
          i18n: nextI18n,
        },
        selectedNode: nextSelected,
        dirty: true,
      }
      return renumberLifecycle(removed_state)
    }

    case 'MOVE_PHASE': {
      const idx = state.definition.phases.findIndex(
        (p) => p.phase_code === action.phaseCode,
      )
      if (idx < 0) return state
      const newIdx = idx + action.direction
      if (newIdx < 0 || newIdx >= state.definition.phases.length) return state
      const next = [...state.definition.phases]
      const [moved] = next.splice(idx, 1)
      if (!moved) return state
      next.splice(newIdx, 0, moved)
      const moved_state: LifecycleDesignerState = {
        ...state,
        definition: {
          ...state.definition,
          phases: reindexPhaseOrders(next),
        },
        dirty: true,
      }
      return renumberLifecycle(moved_state)
    }

    case 'UPDATE_PHASE_STRUCTURAL': {
      const phases = state.definition.phases.map((p) =>
        p.phase_code === action.phaseCode ? { ...p, ...action.patch } : p,
      )
      return {
        ...state,
        definition: { ...state.definition, phases },
        dirty: true,
      }
    }

    case 'UPDATE_PHASE_I18N': {
      const nextDef = updatePhaseI18n(
        state.definition,
        action.locale,
        action.phaseCode,
        (cur) => {
          const base: LifecyclePhaseI18n = cur ?? { phase_code: action.phaseCode }
          return { ...base, ...action.patch }
        },
      )
      return { ...state, definition: nextDef, dirty: true }
    }

    case 'ADD_SUBPHASE': {
      const phase = findPhase(state.definition, action.phaseCode)
      if (!phase) return state
      const subphases = phase.subphases ?? []
      const newCode = generateSubphaseCode(action.phaseCode, subphases)
      const newSubphase: LifecycleSubphase = {
        subphase_code: newCode,
        subphase_order: subphases.length,
      }
      const phases = state.definition.phases.map((p) =>
        p.phase_code === action.phaseCode
          ? { ...p, subphases: [...subphases, newSubphase] }
          : p,
      )
      // Seed i18n entry para esta subphase en cada locale activo.
      let nextDef: LifecycleDefinition = {
        ...state.definition,
        phases,
      }
      for (const loc of Object.keys(state.definition.i18n) as Array<
        SupportedLocale
      >) {
        nextDef = updatePhaseI18n(nextDef, loc, action.phaseCode, (cur) => {
          const base: LifecyclePhaseI18n = cur ?? { phase_code: action.phaseCode }
          return {
            ...base,
            subphases: [
              ...(base.subphases ?? []),
              { subphase_code: newCode, name: newCode },
            ],
          }
        })
      }
      return {
        ...state,
        definition: nextDef,
        selectedNode: {
          kind: 'subphase',
          phaseCode: action.phaseCode,
          subphaseCode: newCode,
        },
        dirty: true,
      }
    }

    case 'REMOVE_SUBPHASE': {
      const phases = state.definition.phases.map((p) => {
        if (p.phase_code !== action.phaseCode) return p
        const subphases = (p.subphases ?? []).filter(
          (sp) => sp.subphase_code !== action.subphaseCode,
        )
        return { ...p, subphases: reindexSubphaseOrders(subphases) }
      })
      // Limpiar i18n subphases.
      let nextDef: LifecycleDefinition = { ...state.definition, phases }
      for (const loc of Object.keys(state.definition.i18n) as Array<
        SupportedLocale
      >) {
        nextDef = updatePhaseI18n(nextDef, loc, action.phaseCode, (cur) => {
          const base: LifecyclePhaseI18n = cur ?? { phase_code: action.phaseCode }
          return {
            ...base,
            subphases: (base.subphases ?? []).filter(
              (sp) => sp.subphase_code !== action.subphaseCode,
            ),
          }
        })
      }
      const nextSelected =
        state.selectedNode &&
        state.selectedNode.kind === 'subphase' &&
        state.selectedNode.subphaseCode === action.subphaseCode
          ? { kind: 'phase' as const, phaseCode: action.phaseCode }
          : state.selectedNode
      const removed_state: LifecycleDesignerState = {
        ...state,
        definition: nextDef,
        selectedNode: nextSelected,
        dirty: true,
      }
      return renumberLifecycle(removed_state)
    }

    case 'MOVE_SUBPHASE': {
      const phases = state.definition.phases.map((p) => {
        if (p.phase_code !== action.phaseCode) return p
        const subphases = [...(p.subphases ?? [])]
        const idx = subphases.findIndex(
          (sp) => sp.subphase_code === action.subphaseCode,
        )
        if (idx < 0) return p
        const newIdx = idx + action.direction
        if (newIdx < 0 || newIdx >= subphases.length) return p
        const [moved] = subphases.splice(idx, 1)
        if (!moved) return p
        subphases.splice(newIdx, 0, moved)
        return { ...p, subphases: reindexSubphaseOrders(subphases) }
      })
      const moved_state: LifecycleDesignerState = {
        ...state,
        definition: { ...state.definition, phases },
        dirty: true,
      }
      return renumberLifecycle(moved_state)
    }

    case 'UPDATE_SUBPHASE_I18N': {
      const nextDef = updatePhaseI18n(
        state.definition,
        action.locale,
        action.phaseCode,
        (cur) => {
          const base: LifecyclePhaseI18n = cur ?? { phase_code: action.phaseCode }
          const subphases = base.subphases ?? []
          const idx = subphases.findIndex(
            (sp) => sp.subphase_code === action.subphaseCode,
          )
          const target: LifecycleSubphaseI18n =
            idx >= 0 && subphases[idx]
              ? { ...subphases[idx]!, ...action.patch }
              : {
                  subphase_code: action.subphaseCode,
                  ...action.patch,
                }
          const nextSubphases =
            idx >= 0
              ? subphases.map((sp, i) => (i === idx ? target : sp))
              : [...subphases, target]
          return { ...base, subphases: nextSubphases }
        },
      )
      return { ...state, definition: nextDef, dirty: true }
    }

    case 'UPDATE_SUBPHASE_STRUCTURAL': {
      // Doc 21 — Fase 3. Mete approval_workflow_template_key dentro
      // del dict de la subphase en `phases[].subphases[]`. Si patch
      // viene con `approval_workflow_template_key: null` (o ''), borra
      // el campo (no lo guarda como null) — así el resolver del
      // backend interpreta "sin workflow declarado" → self-approve.
      const phases = state.definition.phases.map((p) => {
        if (p.phase_code !== action.phaseCode) return p
        const subs = p.subphases ?? []
        const nextSubs = subs.map((sp) => {
          if (sp.subphase_code !== action.subphaseCode) return sp
          const next: LifecycleSubphase = { ...sp }
          if ('approval_workflow_template_key' in action.patch) {
            const v = action.patch.approval_workflow_template_key
            if (v && v.trim()) {
              next['approval_workflow_template_key'] = v.trim()
            } else {
              delete next['approval_workflow_template_key']
            }
          }
          return next
        })
        return { ...p, subphases: nextSubs }
      })
      return {
        ...state,
        definition: { ...state.definition, phases },
        dirty: true,
      }
    }

    case 'ADD_EXPECTED_ARTIFACT': {
      const phases = state.definition.phases.map((p) => {
        if (p.phase_code !== action.phaseCode) return p
        const arts = p.expected_artifacts ?? []
        // Si ya existe esa combinación, no duplicar.
        if (
          arts.some(
            (a) =>
              a.template_key === action.templateKey &&
              a.subphase_code === action.subphaseCode,
          )
        ) {
          return p
        }
        const newArt: ExpectedArtifact = {
          template_key: action.templateKey,
          subphase_code: action.subphaseCode,
          default_count: 1,
          is_optional: false,
        }
        return { ...p, expected_artifacts: [...arts, newArt] }
      })
      return {
        ...state,
        definition: { ...state.definition, phases },
        selectedNode: {
          kind: 'expected_artifact',
          phaseCode: action.phaseCode,
          subphaseCode: action.subphaseCode,
          templateKey: action.templateKey,
        },
        dirty: true,
      }
    }

    case 'REMOVE_EXPECTED_ARTIFACT': {
      const phases = state.definition.phases.map((p) => {
        if (p.phase_code !== action.phaseCode) return p
        return {
          ...p,
          expected_artifacts: (p.expected_artifacts ?? []).filter(
            (a) => a.subphase_code !== action.subphaseCode,
          ),
        }
      })
      const nextSelected =
        state.selectedNode &&
        state.selectedNode.kind === 'expected_artifact' &&
        state.selectedNode.phaseCode === action.phaseCode &&
        state.selectedNode.subphaseCode === action.subphaseCode
          ? { kind: 'phase' as const, phaseCode: action.phaseCode }
          : state.selectedNode
      return {
        ...state,
        definition: { ...state.definition, phases },
        selectedNode: nextSelected,
        dirty: true,
      }
    }

    case 'UPDATE_EXPECTED_ARTIFACT': {
      const phases = state.definition.phases.map((p) => {
        if (p.phase_code !== action.phaseCode) return p
        return {
          ...p,
          expected_artifacts: (p.expected_artifacts ?? []).map((a) =>
            a.subphase_code === action.subphaseCode
              ? { ...a, ...action.patch }
              : a,
          ),
        }
      })
      return {
        ...state,
        definition: { ...state.definition, phases },
        dirty: true,
      }
    }

    case 'ADD_DELIVERABLE': {
      // Crea sub-fase + expected_artifact en una sola transición.
      // El subphase_code se auto-genera usando el helper estándar.
      const phase = findPhase(state.definition, action.phaseCode)
      if (!phase) return state
      const subphases = phase.subphases ?? []
      const newCode = generateSubphaseCode(action.phaseCode, subphases)

      const newSubphase: LifecycleSubphase = {
        subphase_code: newCode,
        subphase_order: subphases.length,
      }
      const newArt: ExpectedArtifact = {
        template_key: action.templateKey,
        subphase_code: newCode,
        default_count: action.defaultCount,
        is_optional: action.isOptional,
        user_can_add_more: action.userCanAddMore,
        ...(action.roleTemplateKey
          ? { role_template_key: action.roleTemplateKey }
          : {}),
      }

      const phases = state.definition.phases.map((p) =>
        p.phase_code === action.phaseCode
          ? {
              ...p,
              subphases: [...subphases, newSubphase],
              expected_artifacts: [...(p.expected_artifacts ?? []), newArt],
            }
          : p,
      )

      // Seed i18n entry para esta sub-fase con el nombre humano que el
      // usuario eligió en cada locale activo del template.
      let nextDef: LifecycleDefinition = {
        ...state.definition,
        phases,
      }
      for (const loc of Object.keys(state.definition.i18n) as Array<
        SupportedLocale
      >) {
        nextDef = updatePhaseI18n(nextDef, loc, action.phaseCode, (cur) => {
          const base: LifecyclePhaseI18n = cur ?? {
            phase_code: action.phaseCode,
          }
          return {
            ...base,
            subphases: [
              ...(base.subphases ?? []),
              { subphase_code: newCode, name: action.subphaseName },
            ],
          }
        })
      }

      return {
        ...state,
        definition: nextDef,
        selectedNode: {
          kind: 'subphase',
          phaseCode: action.phaseCode,
          subphaseCode: newCode,
        },
        dirty: true,
      }
    }

    case 'CLEAR_PHASE_DELIVERABLES': {
      // Limpia subphases + expected_artifacts de una phase, y los
      // entries i18n.subphases del bloque por locale. La selección
      // pasa a la phase si estaba en algún sub-nodo borrado.
      const phases = state.definition.phases.map((p) =>
        p.phase_code === action.phaseCode
          ? { ...p, subphases: [], expected_artifacts: [] }
          : p,
      )
      let nextDef: LifecycleDefinition = { ...state.definition, phases }
      for (const loc of Object.keys(state.definition.i18n) as Array<
        SupportedLocale
      >) {
        nextDef = updatePhaseI18n(nextDef, loc, action.phaseCode, (cur) => {
          const base: LifecyclePhaseI18n = cur ?? {
            phase_code: action.phaseCode,
          }
          return { ...base, subphases: [] }
        })
      }
      const nextSelected =
        state.selectedNode &&
        'phaseCode' in state.selectedNode &&
        state.selectedNode.phaseCode === action.phaseCode &&
        state.selectedNode.kind !== 'phase'
          ? { kind: 'phase' as const, phaseCode: action.phaseCode }
          : state.selectedNode
      return {
        ...state,
        definition: nextDef,
        selectedNode: nextSelected,
        dirty: true,
      }
    }

    case 'REMOVE_DELIVERABLE': {
      // Quita la sub-fase y todos sus expected_artifacts (en el modelo
      // 1:1 del Designer hay solo uno, pero filtramos por seguridad).
      const phases = state.definition.phases.map((p) => {
        if (p.phase_code !== action.phaseCode) return p
        return {
          ...p,
          subphases: (p.subphases ?? []).filter(
            (sp) => sp.subphase_code !== action.subphaseCode,
          ),
          expected_artifacts: (p.expected_artifacts ?? []).filter(
            (a) => a.subphase_code !== action.subphaseCode,
          ),
        }
      })
      // Reindexar subphase_order para que quede 0..N-1.
      const reindexedPhases = phases.map((p) =>
        p.phase_code === action.phaseCode && p.subphases
          ? { ...p, subphases: reindexSubphaseOrders(p.subphases) }
          : p,
      )
      // Limpiar i18n.subphases del locale.
      let nextDef: LifecycleDefinition = {
        ...state.definition,
        phases: reindexedPhases,
      }
      for (const loc of Object.keys(state.definition.i18n) as Array<
        SupportedLocale
      >) {
        nextDef = updatePhaseI18n(nextDef, loc, action.phaseCode, (cur) => {
          const base: LifecyclePhaseI18n = cur ?? {
            phase_code: action.phaseCode,
          }
          return {
            ...base,
            subphases: (base.subphases ?? []).filter(
              (sp) => sp.subphase_code !== action.subphaseCode,
            ),
          }
        })
      }

      const nextSelected =
        state.selectedNode &&
        'subphaseCode' in state.selectedNode &&
        state.selectedNode.subphaseCode === action.subphaseCode
          ? { kind: 'phase' as const, phaseCode: action.phaseCode }
          : state.selectedNode

      const removed_state: LifecycleDesignerState = {
        ...state,
        definition: nextDef,
        selectedNode: nextSelected,
        dirty: true,
      }
      return renumberLifecycle(removed_state)
    }

    default:
      return state
  }
}

// ─── Hook ──────────────────────────────────────────────────────────────────
export function useLifecycleDesignerState(
  initial: LifecycleDefinition,
  defaultLocale: SupportedLocale = 'es',
): [LifecycleDesignerState, Dispatch<LifecycleDesignerAction>] {
  const [state, dispatch] = useReducer(
    lifecycleDesignerReducer,
    undefined,
    (): LifecycleDesignerState => ({
      definition: initial,
      selectedNode: initial.phases[0]
        ? { kind: 'phase', phaseCode: initial.phases[0].phase_code }
        : null,
      activeLocale: defaultLocale,
      dirty: false,
    }),
  )
  return [state, dispatch]
}
